/**
 * Collects every page of a paginated gl-api list.
 *
 * The 1099 list endpoints cap page_size at 200, so any screen that derives a
 * count or a filter from the whole set has to ask for every page. Reading only
 * the first one is how the Vendor Center screens ended up showing counts over
 * the first 200 rows alphabetically while their badges counted the branch.
 *
 * Page 1 reports the total, so the remaining pages are known up front and go
 * out together rather than each waiting on the one before it.
 *
 * `getPage` is passed in rather than an API hook, because the hooks return
 * fresh closures on every render and callers already wrap them.
 */
export interface PagedPayload<T> {
  results?: T[];
  count?: number;
  [key: string]: any;
}

export async function fetchAllPages<T = any>(
  getPage: (page: number) => Promise<PagedPayload<T>>,
  pageSize: number,
): Promise<{ rows: T[]; first: PagedPayload<T> }> {
  const first = await getPage(1);
  const firstRows: T[] = first.results ?? [];
  const count = first.count ?? firstRows.length;

  // A short first page means there is nothing after it. The count check guards
  // the exact-multiple case, where a full page is also the last page.
  if (firstRows.length < pageSize || firstRows.length >= count) {
    return { rows: firstRows, first };
  }

  const remaining = Math.ceil(count / pageSize) - 1;
  const rest = await Promise.all(
    Array.from({ length: remaining }, (_, i) =>
      getPage(i + 2).then((data) => data.results ?? []),
    ),
  );

  // Promise.all preserves order, so the server's sort survives the split.
  return { rows: ([] as T[]).concat(firstRows, ...rest), first };
}
